import * as React from "react";
import { ChevronDown, ChevronLeft, ChevronRight } from "lucide-react";
import { DayPicker, getDefaultClassNames } from "react-day-picker";

import { buttonVariants } from "@/shared/ui/button";
import { cn } from "@/shared/lib/utils";

function Calendar({
  className,
  classNames,
  showOutsideDays = true,
  captionLayout = "label",
  buttonVariant = "ghost",
  formatters,
  components,
  ...props
}: React.ComponentProps<typeof DayPicker> & {
  buttonVariant?: "default" | "destructive" | "outline" | "secondary" | "ghost" | "link";
}) {
  const defaultClassNames = getDefaultClassNames();

  return (
    <DayPicker
      showOutsideDays={showOutsideDays}
      captionLayout={captionLayout}
      className={cn("bg-white p-3 text-neutral-950 dark:bg-popover dark:text-popover-foreground", className)}
      formatters={{
        formatMonthDropdown: (date) => date.toLocaleString("default", { month: "short" }),
        ...formatters,
      }}
      classNames={{
        root: cn("w-fit", defaultClassNames.root),
        months: cn("flex flex-col gap-4", defaultClassNames.months),
        month: cn("flex flex-col gap-4", defaultClassNames.month),
        nav: cn("absolute inset-x-0 top-3 flex items-center justify-between px-3", defaultClassNames.nav),
        button_previous: cn(
          buttonVariants({ variant: buttonVariant, size: "icon-xs" }),
          "size-7 bg-transparent p-0 opacity-60 hover:opacity-100",
          defaultClassNames.button_previous,
        ),
        button_next: cn(
          buttonVariants({ variant: buttonVariant, size: "icon-xs" }),
          "size-7 bg-transparent p-0 opacity-60 hover:opacity-100",
          defaultClassNames.button_next,
        ),
        month_caption: cn("flex h-7 items-center justify-center px-8", defaultClassNames.month_caption),
        caption_label: cn("text-sm font-semibold", defaultClassNames.caption_label),
        weekdays: cn("grid grid-cols-7", defaultClassNames.weekdays),
        weekday: cn("py-2 text-center text-[11px] font-medium text-muted-foreground", defaultClassNames.weekday),
        month_grid: cn("w-full border-collapse", defaultClassNames.month_grid),
        weeks: cn(defaultClassNames.weeks),
        week: cn("grid grid-cols-7", defaultClassNames.week),
        day: cn("relative grid place-items-center p-0 text-center text-sm", defaultClassNames.day),
        day_button: cn(
          buttonVariants({ variant: "ghost", size: "icon" }),
          "size-8 rounded-md p-0 text-xs font-normal aria-selected:opacity-100",
          defaultClassNames.day_button,
        ),
        selected: cn(
          "bg-primary text-black [&>button]:text-black [&>button]:hover:text-black [&>button]:focus:text-black hover:bg-primary hover:text-black focus:bg-primary focus:text-black dark:text-white dark:[&>button]:text-white dark:[&>button]:hover:text-white dark:[&>button]:focus:text-white dark:hover:text-white dark:focus:text-white",
          defaultClassNames.selected,
        ),
        today: cn("border border-primary/50 text-primary", defaultClassNames.today),
        outside: cn("text-muted-foreground/55", defaultClassNames.outside),
        disabled: cn("pointer-events-none text-muted-foreground opacity-60", defaultClassNames.disabled),
        hidden: cn("invisible", defaultClassNames.hidden),
        ...classNames,
      }}
      components={{
        Chevron: ({ className, orientation, ...chevronProps }) => {
          if (orientation === "left") return <ChevronLeft className={cn("size-4", className)} {...chevronProps} />;
          if (orientation === "right") return <ChevronRight className={cn("size-4", className)} {...chevronProps} />;
          return <ChevronDown className={cn("size-4", className)} {...chevronProps} />;
        },
        ...components,
      }}
      {...props}
    />
  );
}

export { Calendar };
